"use client";

import { forwardRef, type CSSProperties, type HTMLAttributes, type Ref } from "react";
import clsx from "clsx";
import { cn } from "@/lib/utils";

export interface TagProps extends Omit<HTMLAttributes<HTMLElement>, "color"> {
  variant?: "plain" | "content";
  as?: "div" | "span";
  color?: string;
}

export const Tag = forwardRef<HTMLElement, TagProps>(
  ({ variant = "plain", as, color, className, style, children, ...props }, ref) => {
    const isContent = variant === "content";
    const element = as ?? (isContent ? "span" : "div");
    const rgb = color
      ? color.replace(/rgb\((\d+), (\d+), (\d+)\)/, "$1 $2 $3") + "/ 0.2"
      : "44 44 44";
    const resolvedStyle = isContent
      ? ({ "--color": color, "--rgb": rgb, ...style } as CSSProperties)
      : style;
    const resolvedClassName = isContent
      ? clsx(
          "p-2 rounded-sm bg-[rgb(var(--rgb))]",
          "font-mono text-(--color) text-[9px] font-semibold tracking-widest uppercase leading-none whitespace-nowrap",
          className,
        )
      : cn(className);

    return element === "span" ? (
      <span ref={ref as Ref<HTMLSpanElement>} data-tag className={resolvedClassName} style={resolvedStyle} {...props}>
        {children}
      </span>
    ) : (
      <div ref={ref as Ref<HTMLDivElement>} data-tag className={resolvedClassName} style={resolvedStyle} {...props}>
        {children}
      </div>
    );
  },
);

Tag.displayName = "Tag";
